import { useState } from "react";
import { Card, Empty, Select, Space, Table, Tag, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import type {
  SuppressionEntry,
  SuppressionsListResponse,
  TenantsListResponse,
} from "@coco-mailrelay/shared-types";
import { apiRequest } from "../lib/apiRequest";

const { Title, Text } = Typography;

const REASON_COLOR: Record<SuppressionEntry["reason"], string> = {
  hard_bounce: "volcano",
  complaint: "magenta",
  manual: "blue",
};

/**
 * Global suppressions view (docs/305 §4.2, nice-to-have). The M8 API exposes
 * suppressions per-tenant only (no cross-tenant endpoint), so this picks a
 * tenant and renders its suppression list — a convenient ops roll-up without
 * inventing a backend contract.
 */
export default function SuppressionsPage(): JSX.Element {
  const [tenantId, setTenantId] = useState<string | undefined>();

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: () => apiRequest<TenantsListResponse>("/tenants"),
  });

  const suppressionsQuery = useQuery({
    queryKey: ["suppressions", tenantId],
    queryFn: () => apiRequest<SuppressionsListResponse>(`/tenants/${tenantId}/suppressions`),
    enabled: Boolean(tenantId),
  });

  return (
    <Card>
      <Title level={4}>Suppressions</Title>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Select
          showSearch
          allowClear
          style={{ width: 360 }}
          placeholder="Select a tenant"
          loading={tenantsQuery.isLoading}
          value={tenantId}
          onChange={setTenantId}
          optionFilterProp="label"
          options={(tenantsQuery.data?.tenants ?? []).map((t) => ({
            value: t.id,
            label: `${t.slug} — ${t.name}`,
          }))}
        />

        {!tenantId ? (
          <Empty description="Pick a tenant to view its suppressions" />
        ) : (
          <>
            <Table<SuppressionEntry>
              rowKey="email"
              loading={suppressionsQuery.isLoading}
              dataSource={suppressionsQuery.data?.items ?? []}
              pagination={false}
              columns={[
                { title: "Email", dataIndex: "email", key: "email" },
                {
                  title: "Reason",
                  dataIndex: "reason",
                  key: "reason",
                  render: (r: SuppressionEntry["reason"]) => <Tag color={REASON_COLOR[r]}>{r}</Tag>,
                },
                { title: "Note", dataIndex: "note", key: "note", render: (n: string | null) => n ?? "—" },
                {
                  title: "Created",
                  dataIndex: "created_at",
                  key: "created_at",
                  render: (v: string) => new Date(v).toLocaleString(),
                },
              ]}
            />
            {suppressionsQuery.data ? <Text type="secondary">Total: {suppressionsQuery.data.total}</Text> : null}
          </>
        )}
      </Space>
    </Card>
  );
}
